import { getSrc, type DynamicImageDimensions } from './dynamic-image';

/** Square DIS crop for gallery `thumbSrc` (zoom dialog 104px, label tab 52px). */
const GALLERY_THUMBNAIL_SIZE = 150;

/** PDP main gallery slot — square, displayed ~full-width on mobile and ~half-width on `lg+`. */
export const IMAGE_GALLERY_MAIN_SIZE = 600;

/** Label tab preview — square, `lg+` only, `2fr` column minus the thumbnail strip. */
export const LABEL_GALLERY_PREVIEW_SIZE = 500;

export const IMAGE_GALLERY_MAIN_WIDTHS: DynamicImageDimensions = { base: IMAGE_GALLERY_MAIN_SIZE };
export const IMAGE_GALLERY_MAIN_HEIGHTS: DynamicImageDimensions = { base: IMAGE_GALLERY_MAIN_SIZE };

export const LABEL_GALLERY_PREVIEW_WIDTHS: DynamicImageDimensions = { base: LABEL_GALLERY_PREVIEW_SIZE };
export const LABEL_GALLERY_PREVIEW_HEIGHTS: DynamicImageDimensions = { base: LABEL_GALLERY_PREVIEW_SIZE };

/**
 * Thumbnail strip: ~20% of gallery width at `lg+` (5-up), peaks near `md` on mobile carousel.
 * The `lg` value is the widest the 5-up slot ever gets at the 600px gallery size.
 */
export const IMAGE_GALLERY_THUMBNAIL_WIDTHS: DynamicImageDimensions = { base: 144, sm: 176, md: 240, lg: 120 };

/**
 * Pre-sizes a gallery image URL to a square DIS crop for `thumbSrc`, so fixed-size `<img>` thumbnails
 * stop downloading the full-resolution asset.
 *
 * The returned URL carries DIS `sw`/`sh` params, which makes it unsuitable for `<DynamicImage widths>`:
 * the responsive `sw` would be rewritten while `sh` stayed at the thumbnail size, and DIS would answer
 * with a cropped, non-square variant. Pass the unsized `src` to those callsites instead.
 */
export const toGalleryThumbnailSrc = (src: string): string =>
    getSrc(src, { w: GALLERY_THUMBNAIL_SIZE, h: GALLERY_THUMBNAIL_SIZE });

/** Zoom dialog main image — square DIS crop per viewport (the dialog switches layout at `md`). */
const GALLERY_ZOOM_MOBILE_SIZE = 600;
const GALLERY_ZOOM_DESKTOP_SIZE = 1200;

/**
 * Fixed-size square URLs for the zoom dialog. Deliberately not `<DynamicImage widths>`: its 2x density
 * candidates would make high-DPI phones download the desktop size.
 */
export const toGalleryZoomSrcs = (src: string): { mobile: string; desktop: string } => ({
    mobile: getSrc(src, { w: GALLERY_ZOOM_MOBILE_SIZE, h: GALLERY_ZOOM_MOBILE_SIZE }),
    desktop: getSrc(src, { w: GALLERY_ZOOM_DESKTOP_SIZE, h: GALLERY_ZOOM_DESKTOP_SIZE }),
});
